import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { api, getAuthHeaders } from "../../../utils/api";
import Select from "react-select";
import { showSuccessToast } from "../../Toast";

/**
 * Common Right Sidebar Component for Asset Forms
 * Used in both Add and Edit modes
 *
 * @param {string} mode - "add" or "edit"
 * @param {boolean} readOnly - Whether fields are read-only (edit mode when not editing)
 * @param {object} values - Formik values
 * @param {function} setFieldValue - Formik setFieldValue
 * @param {object} errors - Formik errors
 * @param {object} touched - Formik touched
 * @param {array} tags - Array of tags
 * @param {function} setTags - Setter for tags
 * @param {string} newTag - Current tag input value
 * @param {function} setNewTag - Setter for newTag
 * @param {function} addTag - Function to add tag
 * @param {function} removeTag - Function to remove tag
 * @param {function} handleTagInput - Handle tag input keydown
 * @param {array} uploadedFiles - Array of uploaded files
 * @param {function} setUploadedFiles - Setter for uploadedFiles
 * @param {function} removeFile - Function to remove file
 * @param {array} uploadedDocuments - Array of uploaded documents
 * @param {boolean} showReminder - Whether to show Set Reminder section (only for add mode)
 * @param {object} reminderProps - Props for Set Reminder section (add mode only)
 * @param {string} subCategory - Add subCategory prop to conditionally show/hide fields
 * @param {string} notesPlaceholder - Configurable notes placeholder
 * @param {string} tagsPlaceholder - Configurable tags placeholder
 * @param {string} linkedLiability - Pre-selected linked liability ID (for edit mode)
 * @param {object} linkedLiabilityInfo - Linked liability info { _id, name } for edit mode prefetch
 */
const RightSidebar = ({
  mode = "add",
  readOnly = false,
  values,
  setFieldValue,
  errors,
  touched,
  tags,
  setTags,
  newTag,
  setNewTag,
  addTag,
  removeTag,
  handleTagInput,
  uploadedFiles,
  setUploadedFiles,
  removeFile,
  uploadedDocuments = [],
  showReminder = false,
  reminderProps = null,
  subCategory = "", // Add subCategory prop to conditionally show/hide fields
  notesPlaceholder = "E.g., Purchase details, specific features, account recovery info...", // Configurable notes placeholder
  tagsPlaceholder = "Add tag and press enter or click add button", // Configurable tags placeholder
  linkedLiability = "", // Pre-selected linked liability (for edit mode)
  linkedLiabilityInfo = null // Linked liability info { _id, name } for edit mode prefetch
}) => {
  // State for Link Liability dropdown
  const [liabilities, setLiabilities] = useState([]);
  const [liabilitiesLoading, setLiabilitiesLoading] = useState(false);
  const [liabilitiesDropdownOpen, setLiabilitiesDropdownOpen] = useState(false);
  const [hasFetchedLiabilities, setHasFetchedLiabilities] = useState(false);

  // Effect to handle pre-selection in edit mode
  React.useEffect(() => {
    if (linkedLiability && !values.linkedLiability) {
      setFieldValue("linkedLiability", linkedLiability);
    }
  }, [linkedLiability, values.linkedLiability, setFieldValue]);

  // Function to fetch available liabilities for linking
  const fetchAvailableLiabilities = useCallback(async () => {
    if (hasFetchedLiabilities || liabilitiesLoading) return;
    
    setLiabilitiesLoading(true);
    try {
      const response = await api.get('/liabilities/available-for-linking', {
        headers: getAuthHeaders(),
      });
      
      // Log response for debugging
      console.log('Liabilities API response:', response);
      
      // Handle various response structures
      let liabilitiesData = [];
      
      if (response.data && Array.isArray(response.data)) {
        liabilitiesData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        liabilitiesData = response.data.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        liabilitiesData = response.data.results;
      } else if (response.data && response.data.data && response.data.data.results && Array.isArray(response.data.data.results)) {
        liabilitiesData = response.data.data.results;
      }
      
      console.log('Parsed liabilities data:', liabilitiesData);
      setLiabilities(liabilitiesData);
      setHasFetchedLiabilities(true);
    } catch (error) {
      console.error('Error fetching available liabilities:', error);
      setLiabilities([]);
    } finally {
      setLiabilitiesLoading(false);
    }
  }, [hasFetchedLiabilities, liabilitiesLoading]);

  // Handle dropdown open - fetch liabilities only when clicked
  const handleLiabilitiesDropdownOpen = () => {
    if (!liabilitiesDropdownOpen) {
      setLiabilitiesDropdownOpen(true);
      fetchAvailableLiabilities();
    }
  };
  // Define which subcategories should NOT show the isActive field
  const hideIsActiveFor = [
    "mutual funds",
    "mutual-funds",
    "fixed deposit",
    "fixed-deposit",
    "stocks",
    "crypto",
    "business ownership",
    "business-ownership",
    "important-papers",
    "important papers",
    "cash",
    "property",
    "collectibles",
    "precious metals",
    "precious-metals",
    "other-physical-assets",
    "other physical assets",
    "otherPhysicalAssets"
  ];
  // File dropzone setup
  const onFilesDrop = React.useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    acceptedFiles.forEach((file) => showSuccessToast(`File added successfully!`));
  }, [setUploadedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onFilesDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    multiple: true,
    disabled: readOnly,
  });

  return (
    <>
      {/* Set Reminder Section - Only for Edit mode */}
   {showReminder && (mode === "edit" || mode === "add") && reminderProps && ( <div className="mb-4"> {reminderProps.children} </div> )}

      {/* Is Active Radio - Conditionally shown based on subCategory */}
      {!hideIsActiveFor.includes(subCategory) && (
        <div className="mb-4">
          <label className="form-label">
            {subCategory == "vehicle" ? 'Is it insured?' : 'Is this active?' }
          </label>
          <div className="radio-group">
            <div className="form-check">
              <input
                type="radio"
                name="isActive"
                value="yes"
                className="form-check-input"
                id="activeYes"
                checked={values.isActive === "yes"}
                onChange={() => setFieldValue("isActive", "yes")}
                disabled={readOnly}
              />
              <label className="form-check-label" htmlFor="activeYes">
                Yes
              </label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                name="isActive"
                value="no"
                className="form-check-input"
                id="activeNo"
                checked={values.isActive === "no"}
                onChange={() => setFieldValue("isActive", "no")}
                disabled={readOnly}
            />
            <label className="form-check-label" htmlFor="activeNo">
              No
            </label>
          </div>
        </div>
        </div>
      )}

      {/* Link Liability - Optional field to link asset to a liability (Multi-Select) */}
      <div className="mb-4">
        <label className="form-label">Link Liability</label>
        <div className="link-liability-dropdown" style={{ position: 'relative' }}>
          <Select
            className="react-select-container"
            classNamePrefix="react-select"
            name="linkedLiability"
            isMulti
            value={values.linkedLiability ? (Array.isArray(values.linkedLiability) ? values.linkedLiability.map(id => ({ value: id, label: liabilities.find(l => l._id === id)?.name || linkedLiabilityInfo?.find(li => li._id === id)?.name || id })) : [{ value: values.linkedLiability, label: liabilities.find(l => l._id === values.linkedLiability)?.name || linkedLiabilityInfo?.name || values.linkedLiability }]) : []}
            onChange={(options) => {
              if (options) {
                const selectedIds = options.map(opt => opt.value);
                setFieldValue("linkedLiability", selectedIds);
              } else {
                setFieldValue("linkedLiability", []);
              }
            }}
            onMenuOpen={handleLiabilitiesDropdownOpen}
            options={liabilities.map(liability => ({
              value: liability._id,
              label: liability.name
            }))}
            placeholder="Select liabilities to link"
            isClearable={true}
            isDisabled={readOnly}
            isLoading={liabilitiesLoading}
          />
        </div>
        {!readOnly && liabilities.length === 0 && !liabilitiesLoading && liabilitiesDropdownOpen && (
          <small className="text-muted">No liabilities available for linking</small>
        )}
      </div>

      {/* Notes */}
      <div className="mb-4">
        <label className="form-label">Notes</label>
        <textarea
          name="notes"
          className="form-control"
          readOnly={readOnly}
          rows={2}
          placeholder={notesPlaceholder}
          value={values.notes}
          onChange={(e) => setFieldValue("notes", e.target.value)}
        />
        {errors.notes && touched.notes && (
          <div className="text-danger small">{errors.notes}</div>
        )}
      </div>

      {/* Tags */}
      <div className="mb-4 tags-field">
        <label className="form-label">Tags</label>
        <div className="tags-input-container">
          <div className="tag-input-group">
            <input
              type="text"
              className="form-control"
              placeholder={tagsPlaceholder}
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleTagInput}
              disabled={readOnly}
            />
            <button
              type="button"
              className="btn btn-add-tag"
              onClick={addTag}
              disabled={readOnly}
            >
              Add
            </button>
          </div>
          <div className="tags-display">
            {tags.map((tag, index) => (
              <span key={index} className="tag-item">
                {tag}
                {!readOnly && (
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={() => removeTag(tag)}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Upload Photos/Documents */}
      <div className="mb-4">
        <label className="form-label mb-3">Upload Photos or Documents</label>
        <div
          {...getRootProps()}
          className={`upload-file-dropzone ${isDragActive ? "drag-active" : ""}`}
        >
          <input {...getInputProps()} />
          <i className="bi bi-plus-lg"></i>
        </div>
        <div className="uploaded-files-list">
          {uploadedFiles.map((file, index) => (
            <div
              key={file.id || file.url || file.originalName || index}
              className="uploaded-file-item mt-4"
            >
              <div className="file-preview">
                {file.preview ||
                (file.url && file.type && file.type.startsWith("image/")) ? (
                  <img
                    src={file.preview || file.url}
                    alt={file.name || file.originalName}
                    className="file-image"
                  />
                ) : (
                  <img
                    src={file.preview || file.url}
                    alt={file.name || file.originalName}
                    className="file-image"
                  />
                )}
              </div>
              <div className="file-info">
                <span className="file-number">{index + 1}. </span>
                <span className="file-name">
                  {file.name || file.originalName}
                </span>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger remove-file"
                  onClick={() =>
                    removeFile(file.id || file.url || file.originalName)
                  }
                >
                  <i className="bi bi-trash"></i>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Documents List (Edit mode only - if documents exist) */}
      {uploadedDocuments.length > 0 && (
        <div className="mb-4">
          <label className="form-label">Documents</label>
          <div>
            {uploadedDocuments.map((doc, idx) => (
              <div key={idx} className="mb-2">
                <i className="bi bi-file-earmark pr-icon"></i>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {doc.originalName || doc.filename || doc.url}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default RightSidebar;
